/**
 * AudioPost — dependency-free client-side audio post-processing for Timbrica's
 * music tools. Pure WebAudio + direct sample math; NO FFmpeg (a 30MB cold-load
 * for trivial DSP is unjustifiable). Shared by music-generator's «🔧 Обработка»
 * (fade / seamless loop / trim → WAV) and the stem mixer (per-stem gain → WAV).
 *
 *   window.AudioPost = {
 *     decode(url) -> Promise<AudioBuffer>          // fetch + decodeAudioData
 *     makeBuf(numCh, length, sr, chans) -> shim    // AudioBuffer-shaped, feeds encodeWav
 *     encodeWav(buffer) -> Blob                     // 16-bit PCM WAV (buffer = real or shim)
 *     fade(buf, inSec, outSec) -> buf               // in-place linear amplitude ramp
 *     trim(buf, startSec, endSec) -> shim           // slice [start,end)
 *     seamlessLoop(buf, crossSec) -> shim           // equal-power tail→head fold (no click)
 *     renderMix(buffers, gains, opts) -> Promise<AudioBuffer>  // OfflineAudioContext sum
 *     mixView(buffers, gains, opts) -> view          // the same sum, read by range (copyFromChannel)
 *   }
 *
 * A "shim" is a lightweight {numberOfChannels, sampleRate, length, getChannelData}
 * object — encodeWav consumes only those, so no live AudioContext is needed to
 * hold intermediate results (fade/trim/loop stay synchronous + memory-lean).
 */
(function () {
    'use strict';

    function AC() { return window.AudioContext || window.webkitAudioContext; }

    // fetch → decodeAudioData → close. Returns a real AudioBuffer (its channel
    // data stays readable after the context closes). Handles MP3 (Suno) + WAV.
    function decode(url) {
        return fetch(url, { credentials: 'same-origin' })
            .then(function (r) { if (!r.ok) { throw new Error('http_' + r.status); } return r.arrayBuffer(); })
            .then(function (ab) {
                var Ctor = AC();
                if (!Ctor) { throw new Error('no_webaudio'); }
                var ctx = new Ctor();
                return ctx.decodeAudioData(ab).then(function (buf) {
                    try { ctx.close(); } catch (e) {}
                    return buf;
                });
            });
    }

    // AudioBuffer-shaped shim consumed by encodeWav (and readable like a buffer).
    function makeBuf(numCh, length, sr, chans) {
        return {
            numberOfChannels: numCh,
            sampleRate: sr,
            length: length,
            getChannelData: function (c) { return chans[c]; }
        };
    }

    function channels(buf) {
        var out = [];
        for (var c = 0; c < buf.numberOfChannels; c++) { out.push(buf.getChannelData(c)); }
        return out;
    }

    /* Every WAV writer below assembles its Blob from pieces of WAV_PIECE frames and reads the
       source through readerFor() — never one buffer of the whole file, and an AudioBuffer-shaped
       VIEW (mixView) is read like a buffer. A 4-minute stereo song at 48 kHz was one contiguous
       45 MB block, and a Windows machine with 2 GB had none left (Sentry TIMBRICA-FRONTEND-NA,
       30.09.2026, /vocal-remover; the same writers serve /drum-remover, /drum-separator and the
       stem mix of /music-generator). Same bytes as the whole-buffer writers they replace
       (tests/js/audio-post-wav-in-pieces.test.mjs) — the 16-bit quantizer (pcm16Quantizer) has a
       fixed seed and runs piece by piece, channel by channel, so the same input is the same bytes. */
    var WAV_PIECE = 65536;

    // reader(c, from, n) → channel c, frames [from, from + n), indexed from 0.
    function readerFor(buf) {
        var own = typeof buf.getChannelData === 'function', chans = [], scratch = [];
        for (var c = 0; c < buf.numberOfChannels; c++) { chans.push(own ? buf.getChannelData(c) : null); scratch.push(null); }
        return function (c, from, n) {
            if (own) { return chans[c].subarray(from, from + n); }
            if (!scratch[c] || scratch[c].length < n) { scratch[c] = new Float32Array(n); }
            var a = scratch[c].length === n ? scratch[c] : scratch[c].subarray(0, n);
            buf.copyFromChannel(a, c, from);
            return a;
        };
    }
    function pieceChannels(read, numCh, from, n) {
        var cs = [];
        for (var c = 0; c < numCh; c++) { cs.push(read(c, from, n)); }
        return cs;
    }

    /* __pcm16:begin
     * float → 16 бит. ОДИН квантователь сайта: этот блок одинаков (с точностью
     * до отступа) в audio-pipeline.js, media-pipeline/audio-native.js,
     * audio-post.js, audio-output-sink.js, audio-recover.js и timbrica-core.js
     * (там — window.__pcm16Quantizer для тулов со своим Int16; сторож —
     * tests/js/pcm16-quantize.test.mjs). Замеры и отвергнутые варианты —
     * docs/why/pcm16-quantize.md.
     *
     * pcm16Quantizer(dither) → q(src, n, dst, at, stride): n выборок канала src
     * пишутся в dst[at], dst[at + stride], … (Int16Array, каналы чередуются).
     *  · Масштаб — как у декодеров Chrome и Firefox (минус ×32768, плюс ×32767):
     *    нетронутый 16-битный звук возвращается бит-в-бит. Прежнее отрезание
     *    `| 0` меняло 27–30 % его выборок на единицу к нулю.
     *  · Блок из 1024 выборок, целиком лежащий на 16-битной сетке (нетронутый
     *    звук, цифровая тишина, выход декодеров с делением на 32768), копируется
     *    точно — без шума и без округления мимо.
     *  · dither=true — TPDF ±1 младший бит (готовый файл: звук тише младшего бита
     *    уходит в шум, а не в тишину и не в гармоники).
     *  · dither=false — округление «половина к чётному», без шума: для того, что
     *    ещё будут складывать (стемы) — сумма четырёх стемов на 9,6 дБ тише по шуму.
     *  · Зерно постоянное: один и тот же вход — одни и те же байты.
     */
    function pcm16Quantizer(dither) {
        var x = 0x9E3779B9, TOL = 1 / 256, BLOCK = 1024;
        // Весь блок лежит на сетке ОДНОГО декодера (плюс ×pos: 32767 — Chrome и
        // Firefox, 32768 — ffmpeg)? Тогда это нетронутый звук: пишем точно.
        // Сетка выбирается на блок: по выборке выход ffmpeg у полной шкалы
        // (32640…32767) ложно садился на сетку 32767 и терял единицу.
        function exact(src, b, e, dst, at, stride, pos) {
            for (var i = b, w = at + b * stride; i < e; i++, w += stride) {
                var s = src[i];
                if (s !== s) s = 0;
                var v = s < 0 ? s * 32768 : s * pos, k = Math.floor(v + 0.5), d = v - k;
                if (d > TOL || d < -TOL) return false;
                dst[w] = k > 32767 ? 32767 : (k < -32768 ? -32768 : k);
            }
            return true;
        }
        return function (src, n, dst, at, stride) {
            // Состояние генератора — в локальной на время вызова: через замыкание
            // горячий цикл вдвое-вчетверо медленнее (замер в Chrome 156).
            var b, e, i, w, s, v, k, r = x, tri = dither;
            for (b = 0; b < n; b += BLOCK) {
                e = b + BLOCK < n ? b + BLOCK : n;
                if (exact(src, b, e, dst, at, stride, 32767) || exact(src, b, e, dst, at, stride, 32768)) continue;
                for (i = b, w = at + b * stride; i < e; i++, w += stride) {
                    s = src[i];
                    if (s !== s) s = 0;
                    v = s < 0 ? s * 32768 : s * 32767;
                    if (tri) {
                        r ^= r << 13; r ^= r >>> 17; r ^= r << 5;
                        k = Math.floor(v + ((r & 0xFFFF) + ((r >>> 16) & 0xFFFF)) * 1.52587890625e-5 - 0.5);  // ×2^-16
                    } else {
                        k = Math.floor(v + 0.5);
                        if (k - v === 0.5 && (k & 1)) k--;
                    }
                    dst[w] = k > 32767 ? 32767 : (k < -32768 ? -32768 : k);
                }
            }
            x = r;
        };
    }
    /* __pcm16:end */

    // Decoded buffer → 16-bit PCM WAV Blob (interleaved). Standalone, no deps.
    function encodeWav(buffer) {
        var numCh = buffer.numberOfChannels, sr = buffer.sampleRate, len = buffer.length;
        var blockAlign = numCh * 2, dataLen = len * blockAlign;
        var head = new ArrayBuffer(44), view = new DataView(head);
        function ws(off, s) { for (var i = 0; i < s.length; i++) { view.setUint8(off + i, s.charCodeAt(i)); } }
        ws(0, 'RIFF'); view.setUint32(4, 36 + dataLen, true); ws(8, 'WAVE');
        ws(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, numCh, true);
        view.setUint32(24, sr, true); view.setUint32(28, sr * blockAlign, true); view.setUint16(32, blockAlign, true); view.setUint16(34, 16, true);
        ws(36, 'data'); view.setUint32(40, dataLen, true);
        // Без шума (прежний контракт encodeWav), но округлением, а не отрезанием.
        var read = readerFor(buffer), parts = [head], q = pcm16Quantizer(false);
        for (var at = 0; at < len; at += WAV_PIECE) {
            var n = Math.min(WAV_PIECE, len - at), pcm = new Int16Array(n * numCh);
            var cs = pieceChannels(read, numCh, at, n);
            for (var c = 0; c < numCh; c++) { q(cs[c], n, pcm, c, numCh); }
            parts.push(pcm.buffer);
        }
        return new Blob(parts, { type: 'audio/wav' });
    }

    // In-place linear amplitude ramp. Same gain per channel (index-based) so the
    // stereo image is preserved. No-op when both durations are 0.
    function fade(buf, inSec, outSec) {
        var sr = buf.sampleRate, N = buf.length, chans = channels(buf);
        var fi = Math.min(N, Math.max(0, Math.round((inSec || 0) * sr)));
        var fo = Math.min(N, Math.max(0, Math.round((outSec || 0) * sr)));
        for (var c = 0; c < buf.numberOfChannels; c++) {
            var d = chans[c];
            for (var i = 0; i < fi; i++) { d[i] *= i / fi; }
            for (var k = 0; k < fo; k++) { d[N - 1 - k] *= k / fo; }
        }
        return buf;
    }

    // Slice [startSec, endSec) of every channel into a fresh shim buffer.
    function trim(buf, startSec, endSec) {
        var sr = buf.sampleRate, N = buf.length;
        var a = Math.max(0, Math.min(N, Math.round((startSec || 0) * sr)));
        var b = Math.max(a + 1, Math.min(N, Math.round((endSec != null ? endSec : N / sr) * sr)));
        var M = b - a, chans = channels(buf), out = [];
        for (var c = 0; c < buf.numberOfChannels; c++) {
            // copy (not subarray view) so the source buffer can be freed
            out.push(chans[c].slice(a, b));
        }
        return makeBuf(buf.numberOfChannels, M, sr, out);
    }

    // Seamless equal-power crossfade loop: fold the TAIL onto the HEAD, dropping
    // the tail, so the file wraps click-free on repeat. Output length = N - L.
    //   • wrap dst[M-1]→dst[0]: connects originally-adjacent samples src[N-L-1]→src[N-L]
    //   • internal join at i=L: connects src[L-1]→src[L]
    //   • sin/cos gains satisfy fadeIn²+fadeOut²=1 → constant energy, no pop/dip
    function seamlessLoop(buf, crossSec) {
        var sr = buf.sampleRate, N = buf.length, numCh = buf.numberOfChannels;
        var L = Math.round((crossSec || 1) * sr);
        var maxL = Math.floor(N / 2) - 1;
        L = Math.max(1, Math.min(maxL, L));
        var M = N - L, chans = channels(buf), out = [];
        for (var c = 0; c < numCh; c++) {
            var src = chans[c], dst = new Float32Array(M);
            // untouched body: input[L .. N-L) → output[L .. M)
            for (var i = L; i < M; i++) { dst[i] = src[i]; }
            // head region output[0..L): equal-power blend of true head + folded tail
            for (var j = 0; j < L; j++) {
                var w = (j + 0.5) / L;
                var fadeIn = Math.sin(w * Math.PI / 2);
                var fadeOut = Math.cos(w * Math.PI / 2);
                dst[j] = src[j] * fadeIn + src[N - L + j] * fadeOut;
            }
            out.push(dst);
        }
        return makeBuf(numCh, M, sr, out);
    }

    /* The same weighted sum, never materialised: copyFromChannel(dest, c, offset) adds up exactly
     * the range it is asked for — the AudioBuffer method of that name, so whatever reads a buffer
     * in pieces (MP3Encoder.encode, AudioPipeline.audioBufferToWav) reads the mix the same way and
     * no whole-length channel of it ever exists. Born of Sentry TIMBRICA-FRONTEND-NA (30.09.2026):
     * /vocal-remover's «save the mix» summed into two whole channels first, and a Windows machine
     * with 2 GB had no 45 MB block left for even one of them.
     * buffers[i] may be null (skipped); gains[i] belongs to buffers[i]. */
    function mixView(buffers, gains, opts) {
        opts = opts || {};
        var live = buffers.filter(Boolean);
        if (!live.length) { throw new Error('no_buffers'); }
        var sr = live[0].sampleRate;
        var maxLen = 0;
        for (var i0 = 0; i0 < live.length; i0++) { if (live[i0].length > maxLen) { maxLen = live[i0].length; } }
        var ch = opts.channels || 2;
        function copyFromChannel(dest, c2, offset) {
            offset = offset || 0;
            if (!(c2 >= 0 && c2 < ch)) { throw new RangeError('mixView: no channel ' + c2 + ' of ' + ch); }
            var n = Math.max(0, Math.min(dest.length, maxLen - offset));
            dest.fill(0, 0, n);
            for (var k = 0; k < buffers.length; k++) {
                var b = buffers[k];
                if (!b) { continue; }
                var g = (gains && typeof gains[k] === 'number') ? gains[k] : 1;
                if (!g) { continue; }
                var nb = b.numberOfChannels;
                // Правила смешения каналов повторяют WebAudio ('speakers'):
                // моно идёт КОПИЕЙ в каждый выход, стерео→моно — ПОЛУСУММА,
                // при равном числе каналов — канал в канал.
                if (nb === 1) {
                    var m = b.getChannelData(0);
                    var nm = Math.min(Math.min(m.length, maxLen) - offset, n);
                    for (var i = 0; i < nm; i++) { dest[i] += m[offset + i] * g; }
                } else if (ch === 1) {
                    var l = b.getChannelData(0), r = b.getChannelData(1);
                    var nl = Math.min(Math.min(l.length, maxLen) - offset, n);
                    for (var j = 0; j < nl; j++) { dest[j] += (l[offset + j] + r[offset + j]) * 0.5 * g; }
                } else {
                    var src = b.getChannelData(c2 < nb ? c2 : nb - 1);
                    var ns = Math.min(Math.min(src.length, maxLen) - offset, n);
                    for (var q = 0; q < ns; q++) { dest[q] += src[offset + q] * g; }
                }
            }
        }
        return { numberOfChannels: ch, sampleRate: sr, length: maxLen, duration: maxLen / sr, copyFromChannel: copyFromChannel };
    }

    // Sum real AudioBuffers through an OfflineAudioContext with per-buffer gains.
    // Output length = longest input (shorter buffers fall silent at their end).
    // buffers[i] must be a REAL AudioBuffer (createBufferSource requirement).
    function renderMix(buffers, gains, opts) {
        opts = opts || {};
        var live = buffers.filter(Boolean);
        if (!live.length) { return Promise.reject(new Error('no_buffers')); }
        var sr = live[0].sampleRate;
        var maxLen = 0;
        for (var i = 0; i < live.length; i++) { if (live[i].length > maxLen) { maxLen = live[i].length; } }
        var Ctor = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        if (!Ctor) { return Promise.reject(new Error('no_offline_ctx')); }
        var ch = opts.channels || 2;

        /* ⚠️ У OfflineAudioContext есть ПОТОЛОК ДЛИНЫ, и он достижим на живых
         * файлах. Замерено Sentry G0 (/vocal-remover, Opera 134):
         * `startRendering failed to create AudioBuffer(2, 265083264, 44100)` —
         * это 100 минут звука, то есть 2.1 ГБ одним куском. Отказ приходил
         * ОТВЕРГНУТЫМ ПРОМИСОМ, вызывающий его не ловил, и кнопка «скачать
         * микс» просто не делала ничего.
         *
         * Граф остаётся основным путём (он проверен и на здоровых файлах
         * ничего не меняем), но там, где он не смог, считаем сумму напрямую:
         * микс с постоянными громкостями — это взвешенное сложение, ради
         * которого аудиограф не нужен. Прямой путь выделяет ровно выходной
         * буфер и потолка не имеет. Это mixView, прочитанный целиком; кому
         * микс нужен только как ФАЙЛ, тот берёт сам mixView и целого буфера
         * не выделяет вовсе. */
        function directSum() {
            var view = mixView(buffers, gains, { channels: ch }), out = [];
            for (var c = 0; c < ch; c++) {
                var a = new Float32Array(maxLen);
                view.copyFromChannel(a, c, 0);
                out.push(a);
            }
            return makeBuf(ch, maxLen, sr, out);
        }

        var octx;
        try {
            octx = new Ctor(ch, maxLen, sr);
        } catch (eCtor) {
            // Некоторые движки отказывают уже в конструкторе.
            return Promise.resolve(directSum());
        }
        for (var k = 0; k < buffers.length; k++) {
            if (!buffers[k]) { continue; }
            var g = octx.createGain();
            g.gain.value = (gains && typeof gains[k] === 'number') ? gains[k] : 1;
            var s = octx.createBufferSource();
            s.buffer = buffers[k];
            s.connect(g); g.connect(octx.destination);
            s.start(0);
        }
        return octx.startRendering().catch(function (e) {
            try {
                if (window.__ae) {
                    window.__ae('feature_use', { f: 'mix_direct_sum', len: maxLen, ch: ch,
                        err: String((e && e.name) || 'Error') });
                }
            } catch (_) {}
            return directSum();
        });
    }

    // Delivery-grade WAV export: 16 / 24-bit PCM or 32-bit float, with optional
    // TPDF dither on the 16-bit path. Dither is required only when reducing bit
    // depth (float/24 -> 16); 24-bit's noise floor (~-144 dB) is inaudible so it
    // ships dither-free, and 32-bit float has no fixed quantization step to
    // decorrelate. TPDF = sum of two independent uniforms => triangular PDF over
    // +/-1 LSB, which makes the quantization error's mean AND variance
    // signal-independent (the correct, artifact-free dither). See BS.1770 export
    // notes. encodeWav() (16-bit, no dither) keeps that contract, rounded since
    // 2026-10-28 instead of truncated (pcm16Quantizer, dither=false).
    function encodeWavBits(buffer, opts) {
        var p = wavParams(buffer, opts), len = buffer.length;
        var head = new ArrayBuffer(44);
        wavHeader(new DataView(head), p);
        var read = readerFor(buffer), parts = [head];
        for (var at = 0; at < len; at += WAV_PIECE) {
            parts.push(wavPiece(read, p, at, Math.min(WAV_PIECE, len - at)));
        }
        return new Blob(parts, { type: 'audio/wav' });
    }

    /* encodeWavBits распилен на header + кадровый проход по куску, чтобы async-
       вариант ниже гнал ТОТ ЖЕ код ломтями. Синхронный вызов на 2-минутном
       треке при слабом CPU держит главный поток ~2.6 с (замер 08.08 при 4×
       дросселе — класс «еле текст ввёл» при пакетном мастеринге). */
    function wavParams(buffer, opts) {
        opts = opts || {};
        var bits = opts.bitDepth === 24 ? 24 : opts.bitDepth === 32 ? 32 : 16;
        var p = {
            bits: bits, isFloat: bits === 32,
            // Dither only meaningful for 16-bit; default ON there, OFF elsewhere.
            dither: (typeof opts.dither === 'boolean') ? opts.dither : (bits === 16),
            numCh: buffer.numberOfChannels, sr: buffer.sampleRate
        };
        p.blockAlign = p.numCh * (bits / 8); p.dataLen = buffer.length * p.blockAlign;
        if (bits === 16) { p.q = pcm16Quantizer(p.dither); }
        return p;
    }
    function wavHeader(view, opts) {
        function ws(off, s) { for (var i = 0; i < s.length; i++) { view.setUint8(off + i, s.charCodeAt(i)); } }
        ws(0, 'RIFF'); view.setUint32(4, 36 + opts.dataLen, true); ws(8, 'WAVE');
        ws(12, 'fmt '); view.setUint32(16, 16, true);
        view.setUint16(20, opts.isFloat ? 3 : 1, true);       // 1 = PCM, 3 = IEEE float
        view.setUint16(22, opts.numCh, true);
        view.setUint32(24, opts.sr, true); view.setUint32(28, opts.sr * opts.blockAlign, true);
        view.setUint16(32, opts.blockAlign, true); view.setUint16(34, opts.bits, true);
        ws(36, 'data'); view.setUint32(40, opts.dataLen, true);
    }
    // Frames [at, at + n) of the source → their own ArrayBuffer of n * blockAlign bytes.
    function wavPiece(read, p, at, n) {
        var piece = new ArrayBuffer(n * p.blockAlign), view = new DataView(piece);
        var chans = pieceChannels(read, p.numCh, at, n);
        if (p.bits === 16) {
            var pcm16 = new Int16Array(piece);
            for (var c16 = 0; c16 < p.numCh; c16++) { p.q(chans[c16], n, pcm16, c16, p.numCh); }
            return piece;
        }
        var off = 0;
        for (var i = 0; i < n; i++) {
            for (var c = 0; c < p.numCh; c++) {
                var s = chans[c][i];
                if (p.isFloat) {
                    if (s > 1) s = 1; else if (s < -1) s = -1;
                    view.setFloat32(off, s, true); off += 4;
                } else if (p.bits === 24) {
                    // ×8388608 — масштаб декодеров 24 бит; с ×8388607 нетронутый
                    // 24-битный файл возвращался бит-в-бит на 94 % (замер 28.10).
                    var v24 = Math.round(s * 8388608);
                    if (v24 > 8388607) v24 = 8388607; else if (!(v24 >= -8388608)) v24 = v24 < 0 ? -8388608 : 0;
                    if (v24 < 0) v24 += 0x1000000;
                    view.setUint8(off, v24 & 0xff); view.setUint8(off + 1, (v24 >> 8) & 0xff); view.setUint8(off + 2, (v24 >> 16) & 0xff);
                    off += 3;
                }
            }
        }
        return piece;
    }
    // Same bytes as encodeWavBits (the 16-bit quantizer is seeded, not random), sliced
    // under a time budget with a REAL macrotask between slices — a promise
    // chain would be microtasks and one solid block (the folder-zip lesson).
    async function encodeWavBitsAsync(buffer, opts) {
        opts = opts || {};
        var p = wavParams(buffer, opts), len = buffer.length;
        var head = new ArrayBuffer(44);
        wavHeader(new DataView(head), p);
        var read = readerFor(buffer), parts = [head];
        var SLICE_MS = 24, pos = 0;
        while (pos < len) {
            var t0 = performance.now();
            do {
                var n = Math.min(WAV_PIECE, len - pos);
                parts.push(wavPiece(read, p, pos, n));
                pos += n;
            } while (pos < len && performance.now() - t0 < SLICE_MS);
            if (opts.onProgress) { try { opts.onProgress(pos / len); } catch (e) {} }
            await new Promise(function (r) {
                if (typeof scheduler !== 'undefined' && scheduler.yield) { scheduler.yield().then(r); return; }
                setTimeout(r, 0);
            });
        }
        return new Blob(parts, { type: 'audio/wav' });
    }

    window.AudioPost = {
        decode: decode,
        makeBuf: makeBuf,
        encodeWav: encodeWav,
        encodeWavBits: encodeWavBits,
        encodeWavBitsAsync: encodeWavBitsAsync,
        fade: fade,
        trim: trim,
        seamlessLoop: seamlessLoop,
        mixView: mixView,
        renderMix: renderMix
    };
})();
